import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  VStack,
  HStack,
  IconButton,
  useToast,
  Text,
  Heading,
  Badge,
  InputGroup,
  InputLeftElement,
  Grid,
  GridItem,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Flex,
  Card,
  CardHeader,
  CardBody,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
} from '@chakra-ui/react';
import { EditIcon, AddIcon, SearchIcon, CalendarIcon, WarningIcon } from '@chakra-ui/icons';
import { FaArchive, FaUndo, FaEye, FaEyeSlash } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { getLabTimelineStatus, getLabTimelineConfig, getLabTimelineOrder } from '../../utils/statusBadge';
import { useOutletContext } from 'react-router-dom';

const LIFECYCLE_OPTIONS = getLabTimelineOrder().map((code) => ({
  code,
  label: getLabTimelineConfig(code).label,
}));

const PatientManagement = () => {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientInsurance, setPatientInsurance] = useState([]);
  const [patientSubmissions, setPatientSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isInsuranceOpen, onOpen: onInsuranceOpen, onClose: onInsuranceClose } = useDisclosure();
  const toast = useToast();
  const { adminUser } = useOutletContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    patient_email: '',
    patient_address: '',
    patient_dob: '',
    patient_phone: '',
  });

  const [insuranceFormData, setInsuranceFormData] = useState({
    insurance_company: '',
    policy_number: '',
    group_number: '',
    member_id: '',
    insurance_phone: '',
    insurance_address: '',
    effective_date: '',
    expiration_date: '',
    is_primary: true,
    is_active: true,
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  // Fetch patients from database
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const viewName = showArchived ? 'all_patients_with_current_insurance' : 'patients_with_current_insurance';
      const { data, error } = await supabase
        .from(viewName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPatients(data || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast({
        title: 'Error fetching patients',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, showArchived]);

  // Fetch patient insurance history
  const fetchPatientInsurance = useCallback(async (patientId) => {
    if (!patientId) return;
    try {
      const { data, error } = await supabase
        .from('patient_insurance')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPatientInsurance(data || []);
    } catch (error) {
      console.error('Error fetching patient insurance:', error);
    }
  }, []);

  // Fetch patient submissions history
  const fetchPatientSubmissions = useCallback(async (patientId) => {
    if (!patientId) return;
    try {
      const { data, error } = await supabase
        .from('submissions')
        .select(`
          *,
          labs!inner(name, contact_email),
          phlebotomist_profiles(full_name, company_name, email),
          submission_batches(id, shipped_at)
        `)
        .eq('patient_id', patientId)
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      setPatientSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching patient submissions:', error);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients, showArchived]);

  useEffect(() => {
    if (selectedPatient) {
      setDetailLoading(true);
      Promise.all([
        fetchPatientInsurance(selectedPatient.id),
        fetchPatientSubmissions(selectedPatient.id)
      ]).finally(() => setDetailLoading(false));
    }
  }, [selectedPatient, fetchPatientInsurance, fetchPatientSubmissions]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInsuranceInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInsuranceFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast({
        title: 'Error',
        description: 'First name and last name are required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const patientData = {
        ...formData,
        patient_name: `${formData.first_name} ${formData.last_name}`.trim(), // Maintain backward compatibility
        created_by_user: adminUser?.name || 'Admin',
        updated_by_user: adminUser?.name || 'Admin',
      };

      let result;
      if (selectedPatient && formData.id) {
        // Update existing patient
        const { data, error } = await supabase
          .from('patients')
          .update(patientData)
          .eq('id', selectedPatient.id)
          .select();
        
        if (error) throw error;
        result = data[0];
        
        toast({
          title: 'Patient updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new patient
        const { data, error } = await supabase
          .from('patients')
          .insert([patientData])
          .select();
        
        if (error) throw error;
        result = data[0];
        
        toast({
          title: 'Patient created successfully',
          description: `Patient ID: ${result.patient_id}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      await fetchPatients();
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error saving patient:', error);
      toast({
        title: 'Error saving patient',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInsuranceSubmit = async (e) => {
    e.preventDefault();
    
    if (!insuranceFormData.insurance_company.trim()) {
      toast({
        title: 'Error',
        description: 'Insurance company is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const insuranceData = {
        ...insuranceFormData,
        patient_id: selectedPatient.id,
        created_by_user: adminUser?.name || 'Admin',
        updated_by_user: adminUser?.name || 'Admin',
      };

      const { error } = await supabase
        .from('patient_insurance')
        .insert([insuranceData]);

      if (error) throw error;

      toast({
        title: 'Insurance added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await fetchPatientInsurance(selectedPatient.id);
      await fetchPatients(); // Refresh to update current insurance display
      resetInsuranceForm();
      onInsuranceClose();
    } catch (error) {
      console.error('Error saving insurance:', error);
      toast({
        title: 'Error saving insurance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      patient_email: '',
      patient_address: '',
      patient_dob: '',
      patient_phone: '',
    });
  };

  const resetInsuranceForm = () => {
    setInsuranceFormData({
      insurance_company: '',
      policy_number: '',
      group_number: '',
      member_id: '',
      insurance_phone: '',
      insurance_address: '',
      effective_date: '',
      expiration_date: '',
      is_primary: true,
      is_active: true,
    });
  };

  const handleEditPatient = (patient) => {
    setFormData({
      id: patient.id,
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      patient_email: patient.patient_email || '',
      patient_address: patient.patient_address || '',
      patient_dob: patient.patient_dob || '',
      patient_phone: patient.patient_phone || '',
    });
    onOpen();
  };

  const handleArchivePatient = async (patient) => {
    const isArchived = patient.archived;
    const action = isArchived ? 'unarchive' : 'archive';
    const confirmMessage = isArchived 
      ? 'Are you sure you want to unarchive this patient? They will appear in the active patient list again.'
      : 'Are you sure you want to archive this patient? They will be hidden from the main patient list but all records will be preserved.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const functionName = isArchived ? 'unarchive_patient' : 'archive_patient';
      const { error } = await supabase.rpc(functionName, {
        patient_uuid: patient.id,
        [`${isArchived ? 'unarchived' : 'archived'}_by_user`]: adminUser?.name || 'Admin'
      });

      if (error) throw error;

      toast({
        title: `Patient ${action}d successfully`,
        description: isArchived 
          ? 'Patient is now active and visible in the main list'
          : 'Patient has been archived and removed from the main list',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (selectedPatient?.id === patient.id && !isArchived) {
        setSelectedPatient(null); // Clear selection if archiving the currently selected patient
      }
      await fetchPatients();
    } catch (error) {
      console.error(`Error ${action}ing patient:`, error);
      toast({
        title: `Error ${action}ing patient`,
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const toggleInsuranceStatus = async (insuranceId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('patient_insurance')
        .update({ is_active: !currentStatus })
        .eq('id', insuranceId);

      if (error) throw error;

      await fetchPatientInsurance(selectedPatient.id);
      await fetchPatients();
      
      toast({
        title: `Insurance ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating insurance status:', error);
      toast({
        title: 'Error updating insurance status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Filter patients based on search query
  const filteredPatients = useMemo(() => {
    if (!searchQuery.trim()) return patients;
    
    const query = searchQuery.toLowerCase();
    return patients.filter(patient => 
      patient.first_name?.toLowerCase().includes(query) ||
      patient.last_name?.toLowerCase().includes(query) ||
      patient.full_name?.toLowerCase().includes(query) ||
      patient.patient_email?.toLowerCase().includes(query) ||
      patient.patient_id?.toLowerCase().includes(query) ||
      patient.patient_phone?.includes(query)
    );
  }, [patients, searchQuery]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const isInsuranceExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  const isInsuranceExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const today = new Date();
    const expDate = new Date(expirationDate);
    const daysUntilExp = (expDate - today) / (1000 * 60 * 60 * 24);
    return daysUntilExp > 0 && daysUntilExp <= 30;
  };

  if (loading) {
    return (
      <Box p={8} display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh">
      <Flex h="100vh" overflow="hidden">
        {/* Left Panel - Patient List */}
        <Box w="400px" bg={cardBg} borderRight="1px" borderColor={borderColor} overflowY="auto">
          <Box p={4} borderBottom="1px" borderColor={borderColor}>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Patients</Heading>
              <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={() => { resetForm(); onOpen(); }}>
                Add Patient
              </Button>
            </Flex>
            
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="sm"
              />
            </InputGroup>

            <HStack mt={2} spacing={2} justify="space-between">
              <Badge colorScheme="blue" fontSize="xs">
                Total: {patients.length}
              </Badge>
              
              <IconButton
                icon={showArchived ? <FaEyeSlash /> : <FaEye />}
                size="xs"
                variant="outline"
                colorScheme={showArchived ? "gray" : "blue"}
                onClick={() => setShowArchived(!showArchived)}
                title={showArchived ? "Hide archived patients" : "Show archived patients"}
              />
            </HStack>
          </Box>

          <VStack spacing={0} align="stretch">
            {filteredPatients.length === 0 ? (
              <Box p={4}>
                <Alert status="info" size="sm">
                  <AlertIcon />
                  No patients found
                </Alert>
              </Box>
            ) : (
              filteredPatients.map((patient) => (
                                 <Box
                   key={patient.id}
                   p={4}
                   borderBottom="1px"
                   borderColor={borderColor}
                   cursor="pointer"
                   bg={selectedPatient?.id === patient.id ? 'blue.50' : patient.archived ? 'gray.50' : 'transparent'}
                   _hover={{ bg: selectedPatient?.id === patient.id ? 'blue.50' : patient.archived ? 'gray.100' : 'gray.50' }}
                   onClick={() => setSelectedPatient(patient)}
                   opacity={patient.archived ? 0.7 : 1}
                 >
                  <Flex justify="space-between" align="center" mb={2}>
                                         <VStack align="start" spacing={0}>
                       <HStack spacing={2} align="center">
                         <Text fontWeight="medium" fontSize="sm" color={patient.archived ? 'gray.500' : 'inherit'}>
                           {patient.full_name}
                         </Text>
                         {patient.archived && (
                           <Badge colorScheme="gray" size="sm">
                             ARCHIVED
                           </Badge>
                         )}
                       </HStack>
                       <Badge colorScheme="purple" fontSize="xs" fontWeight="bold">
                         {patient.patient_id}
                       </Badge>
                     </VStack>
                                         <HStack spacing={1}>
                       <IconButton
                         icon={<EditIcon />}
                         size="xs"
                         variant="ghost"
                         onClick={(e) => { e.stopPropagation(); handleEditPatient(patient); }}
                         isDisabled={patient.archived}
                       />
                       <IconButton
                         icon={patient.archived ? <FaUndo /> : <FaArchive />}
                         size="xs"
                         variant="ghost"
                         colorScheme={patient.archived ? "green" : "orange"}
                         onClick={(e) => { e.stopPropagation(); handleArchivePatient(patient); }}
                         title={patient.archived ? "Unarchive patient" : "Archive patient"}
                       />
                     </HStack>
                  </Flex>
                  
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.500">
                      {patient.patient_email || 'No email'}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {patient.patient_phone || 'No phone'}
                    </Text>
                    {patient.current_insurance_company && (
                      <Badge 
                        size="sm" 
                        colorScheme={isInsuranceExpired(patient.current_insurance_expiration) ? 'red' : 'green'}
                      >
                        {patient.current_insurance_company}
                      </Badge>
                    )}
                  </VStack>
                </Box>
              ))
            )}
          </VStack>
        </Box>

        {/* Right Panel - Patient Details */}
        <Box flex={1} overflowY="auto">
          {selectedPatient ? (
            <Box p={6}>
              {detailLoading ? (
                <Flex justify="center" align="center" h="200px">
                  <Spinner />
                </Flex>
              ) : (
                <>
                  {/* Patient Header */}
                                     <Flex justify="space-between" align="center" mb={6}>
                     <VStack align="start" spacing={1}>
                       <HStack spacing={3} align="center">
                         <Heading size="lg" color={selectedPatient.archived ? 'gray.500' : 'inherit'}>
                           {selectedPatient.full_name}
                         </Heading>
                         {selectedPatient.archived && (
                           <Badge colorScheme="gray" fontSize="md">
                             ARCHIVED
                           </Badge>
                         )}
                       </HStack>
                       <HStack>
                         <Badge colorScheme="purple" fontSize="md" fontWeight="bold">
                           {selectedPatient.patient_id}
                         </Badge>
                         <Badge colorScheme="gray">
                           {selectedPatient.total_submissions || 0} submissions
                         </Badge>
                       </HStack>
                       {selectedPatient.archived && selectedPatient.archived_at && (
                         <Text fontSize="sm" color="gray.500">
                           Archived on {formatDate(selectedPatient.archived_at)} 
                           {selectedPatient.archived_by && ` by ${selectedPatient.archived_by}`}
                         </Text>
                       )}
                     </VStack>
                     <VStack spacing={2}>
                       {selectedPatient.archived ? (
                         <Button
                           leftIcon={<FaUndo />}
                           colorScheme="green"
                           onClick={() => handleArchivePatient(selectedPatient)}
                         >
                           Unarchive Patient
                         </Button>
                       ) : (
                         <>
                           <Button
                             leftIcon={<AddIcon />}
                             colorScheme="green"
                             onClick={() => { resetInsuranceForm(); onInsuranceOpen(); }}
                           >
                             Add Insurance
                           </Button>
                           <Button
                             leftIcon={<FaArchive />}
                             colorScheme="orange"
                             variant="outline"
                             size="sm"
                             onClick={() => handleArchivePatient(selectedPatient)}
                           >
                             Archive Patient
                           </Button>
                         </>
                       )}
                     </VStack>
                   </Flex>

                  {/* Patient Basic Info */}
                  <Card mb={6}>
                    <CardHeader>
                      <Heading size="md">Patient Information</Heading>
                    </CardHeader>
                    <CardBody>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm" color="gray.500">Email</Text>
                          <Text>{selectedPatient.patient_email || 'Not provided'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm" color="gray.500">Phone</Text>
                          <Text>{selectedPatient.patient_phone || 'Not provided'}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm" color="gray.500">Date of Birth</Text>
                          <Text>{formatDate(selectedPatient.patient_dob)}</Text>
                        </Box>
                        <Box>
                          <Text fontWeight="bold" fontSize="sm" color="gray.500">Created</Text>
                          <Text>{formatDate(selectedPatient.created_at)}</Text>
                        </Box>
                        {selectedPatient.patient_address && (
                          <Box gridColumn={{ md: 'span 2' }}>
                            <Text fontWeight="bold" fontSize="sm" color="gray.500">Address</Text>
                            <Text>{selectedPatient.patient_address}</Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    </CardBody>
                  </Card>

                  <Tabs>
                    <TabList>
                      <Tab>Insurance History</Tab>
                      <Tab>Submission History</Tab>
                    </TabList>

                    <TabPanels>
                      {/* Insurance Tab */}
                      <TabPanel>
                        <Card>
                          <CardHeader>
                            <Flex justify="space-between" align="center">
                              <Heading size="md">Insurance Records</Heading>
                              <Badge colorScheme="blue">
                                {patientInsurance.length} records
                              </Badge>
                            </Flex>
                          </CardHeader>
                          <CardBody>
                            {patientInsurance.length === 0 ? (
                              <Alert status="info">
                                <AlertIcon />
                                No insurance records found for this patient.
                              </Alert>
                            ) : (
                              <TableContainer>
                                <Table size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>Company</Th>
                                      <Th>Policy #</Th>
                                      <Th>Effective</Th>
                                      <Th>Expires</Th>
                                      <Th>Status</Th>
                                      <Th>Actions</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {patientInsurance.map((insurance) => (
                                      <Tr key={insurance.id}>
                                        <Td>
                                          <VStack align="start" spacing={0}>
                                            <Text fontWeight="medium">{insurance.insurance_company}</Text>
                                            {insurance.is_primary && (
                                              <Badge colorScheme="blue" size="sm">Primary</Badge>
                                            )}
                                          </VStack>
                                        </Td>
                                        <Td>{insurance.policy_number || '-'}</Td>
                                        <Td>{formatDate(insurance.effective_date)}</Td>
                                        <Td>
                                          <VStack align="start" spacing={0}>
                                            <Text>{formatDate(insurance.expiration_date)}</Text>
                                            {insurance.expiration_date && (
                                              <>
                                                {isInsuranceExpired(insurance.expiration_date) && (
                                                  <Badge colorScheme="red" size="sm">
                                                    <WarningIcon mr={1} />
                                                    Expired
                                                  </Badge>
                                                )}
                                                {isInsuranceExpiringSoon(insurance.expiration_date) && 
                                                 !isInsuranceExpired(insurance.expiration_date) && (
                                                  <Badge colorScheme="yellow" size="sm">
                                                    <CalendarIcon mr={1} />
                                                    Expiring Soon
                                                  </Badge>
                                                )}
                                              </>
                                            )}
                                          </VStack>
                                        </Td>
                                        <Td>
                                          <Badge colorScheme={insurance.is_active ? 'green' : 'gray'}>
                                            {insurance.is_active ? 'Active' : 'Inactive'}
                                          </Badge>
                                        </Td>
                                        <Td>
                                          <Button
                                            size="xs"
                                            variant="outline"
                                            colorScheme={insurance.is_active ? 'red' : 'green'}
                                            onClick={() => toggleInsuranceStatus(insurance.id, insurance.is_active)}
                                          >
                                            {insurance.is_active ? 'Deactivate' : 'Activate'}
                                          </Button>
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            )}
                          </CardBody>
                        </Card>
                      </TabPanel>

                      {/* Submissions Tab */}
                      <TabPanel>
                        <Card>
                          <CardHeader>
                            <Flex justify="space-between" align="center">
                              <Heading size="md">Submission History</Heading>
                              <Badge colorScheme="blue">
                                {patientSubmissions.length} submissions
                              </Badge>
                            </Flex>
                          </CardHeader>
                          <CardBody>
                            {patientSubmissions.length === 0 ? (
                              <Alert status="info">
                                <AlertIcon />
                                No submissions found for this patient.
                              </Alert>
                            ) : (
                              <TableContainer>
                                <Table size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>Date</Th>
                                      <Th>Doctor</Th>
                                      <Th>Phlebotomist</Th>
                                      <Th>Laboratory</Th>
                                      <Th>Status</Th>
                                      <Th>Actions</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {patientSubmissions.map((submission) => (
                                      <Tr key={submission.id}>
                                        <Td>{formatDate(submission.submitted_at)}</Td>
                                        <Td>{submission.doctor_name || 'Not specified'}</Td>
                                        <Td>
                                          {submission.phlebotomist_profiles ? 
                                            (submission.phlebotomist_profiles.company_name || 
                                             submission.phlebotomist_profiles.full_name) : 
                                            'Not assigned'
                                          }
                                        </Td>
                                        <Td>
                                          {submission.labs ? submission.labs.name : 'Not specified'}
                                        </Td>
                                        <Td>
                                          {(() => {
                                            const stage = getLabTimelineStatus(submission, submission.submission_batches);
                                            return (
                                              <HStack spacing={2}>
                                                <Box w="6px" h="6px" borderRadius="full" bg={`${stage.color}.400`} flexShrink={0} />
                                                <Select
                                                  size="xs"
                                                  value={stage.code}
                                                  width="auto"
                                                  minW="170px"
                                                  bg="white"
                                                  borderColor="gray.200"
                                                  onChange={async (e) => {
                                                    const newCode = e.target.value;
                                                    const { error } = await supabase
                                                      .from('submissions')
                                                      .update({ lifecycle_override: newCode })
                                                      .eq('id', submission.id);
                                                    if (error) {
                                                      toast({ title: 'Could not update status', description: error.message, status: 'error' });
                                                    } else {
                                                      toast({ title: `Set to ${getLabTimelineConfig(newCode).label}`, status: 'success', duration: 2000 });
                                                      if (selectedPatient) fetchPatientSubmissions(selectedPatient.id);
                                                    }
                                                  }}
                                                >
                                                  {LIFECYCLE_OPTIONS.map((opt) => (
                                                    <option key={opt.code} value={opt.code}>{opt.label}</option>
                                                  ))}
                                                </Select>
                                              </HStack>
                                            );
                                          })()}
                                        </Td>
                                        <Td>
                                          <Button
                                            size="xs"
                                            variant="outline"
                                            colorScheme="blue"
                                            onClick={() => window.open(`/admin/submissions/${submission.id}`, '_blank')}
                                          >
                                            View Details
                                          </Button>
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            )}
                          </CardBody>
                        </Card>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </>
              )}
            </Box>
          ) : (
            <Box p={6} textAlign="center" color="gray.500">
              <Heading size="md" mb={2}>No Patient Selected</Heading>
              <Text>Select a patient from the left panel to view details</Text>
            </Box>
          )}
        </Box>
      </Flex>

      {/* Add/Edit Patient Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formData.id ? 'Edit Patient' : 'Add New Patient'}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handlePatientSubmit}>
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <Grid templateColumns="1fr 1fr" gap={4} width="100%">
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>First Name</FormLabel>
                      <Input
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        placeholder="First name"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        placeholder="Last name"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>

                <Grid templateColumns="1fr 1fr" gap={4} width="100%">
                  <GridItem>
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input
                        name="patient_email"
                        type="email"
                        value={formData.patient_email}
                        onChange={handleInputChange}
                        placeholder="patient@email.com"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Phone</FormLabel>
                      <Input
                        name="patient_phone"
                        type="tel"
                        value={formData.patient_phone}
                        onChange={handleInputChange}
                        placeholder="+1 844-718-7800"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>

                <FormControl>
                  <FormLabel>Address</FormLabel>
                  <Input
                    name="patient_address"
                    value={formData.patient_address}
                    onChange={handleInputChange}
                    placeholder="Enter patient address"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Date of Birth</FormLabel>
                  <Input
                    name="patient_dob"
                    type="date"
                    value={formData.patient_dob}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="blue" mr={3} type="submit">
                {formData.id ? 'Update Patient' : 'Create Patient'}
              </Button>
              <Button onClick={() => { resetForm(); onClose(); }}>Cancel</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Add Insurance Modal */}
      <Modal isOpen={isInsuranceOpen} onClose={onInsuranceClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Insurance for {selectedPatient?.full_name}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleInsuranceSubmit}>
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <Grid templateColumns="1fr 1fr" gap={4} width="100%">
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Insurance Company</FormLabel>
                      <Input
                        name="insurance_company"
                        value={insuranceFormData.insurance_company}
                        onChange={handleInsuranceInputChange}
                        placeholder="Insurance provider"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Policy Number</FormLabel>
                      <Input
                        name="policy_number"
                        value={insuranceFormData.policy_number}
                        onChange={handleInsuranceInputChange}
                        placeholder="Policy number"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>

                <Grid templateColumns="1fr 1fr" gap={4} width="100%">
                  <GridItem>
                    <FormControl>
                      <FormLabel>Group Number</FormLabel>
                      <Input
                        name="group_number"
                        value={insuranceFormData.group_number}
                        onChange={handleInsuranceInputChange}
                        placeholder="Group number"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Member ID</FormLabel>
                      <Input
                        name="member_id"
                        value={insuranceFormData.member_id}
                        onChange={handleInsuranceInputChange}
                        placeholder="Member ID"
                      />
                    </FormControl>
                  </GridItem>
                </Grid>

                <Grid templateColumns="1fr 1fr" gap={4} width="100%">
                  <GridItem>
                    <FormControl>
                      <FormLabel>Effective Date</FormLabel>
                      <Input
                        name="effective_date"
                        type="date"
                        value={insuranceFormData.effective_date}
                        onChange={handleInsuranceInputChange}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Expiration Date</FormLabel>
                      <Input
                        name="expiration_date"
                        type="date"
                        value={insuranceFormData.expiration_date}
                        onChange={handleInsuranceInputChange}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>

                <FormControl>
                  <FormLabel>Insurance Phone</FormLabel>
                  <Input
                    name="insurance_phone"
                    type="tel"
                    value={insuranceFormData.insurance_phone}
                    onChange={handleInsuranceInputChange}
                    placeholder="Insurance company phone"
                  />
                </FormControl>

                <Grid templateColumns="1fr 1fr" gap={4} width="100%">
                  <GridItem>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel mb="0">Primary Insurance</FormLabel>
                      <Checkbox
                        name="is_primary"
                        isChecked={insuranceFormData.is_primary}
                        onChange={handleInsuranceInputChange}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel mb="0">Active</FormLabel>
                      <Checkbox
                        name="is_active"
                        isChecked={insuranceFormData.is_active}
                        onChange={handleInsuranceInputChange}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="green" mr={3} type="submit">
                Add Insurance
              </Button>
              <Button onClick={() => { resetInsuranceForm(); onInsuranceClose(); }}>Cancel</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PatientManagement; 